import {
    ClaimWorkflowConfiguration,
    ClaimWorkflowConfigurationEditPayload,
    CoreFetch,
    CreateClaimWorkflowConfigurationRequest,
    WorkerApi,
} from '../../api';
import { WorkerConfigurationApiMode, fetchClaimWorkflowConfiguration } from './worker-configuration.api';
import {
    cloneWorkflowConfigMap,
    isUnsetConfigValue,
    parseWorkerWorkflowConfiguration,
    WorkerWorkflowConfiguration,
    WorkflowConfigMap,
    WorkflowConfigValue,
} from './worker-configuration.types';

type ClaimWorkflowConfigurationRequestBody = Record<string, Record<string, unknown>>;

function toPayloadValue(value: WorkflowConfigValue | undefined): WorkflowConfigValue | null {
    if (isUnsetConfigValue(value)) {
        return null;
    }

    return value;
}

function configValuesEqual(left: WorkflowConfigValue | undefined, right: WorkflowConfigValue | undefined): boolean {
    if (left === right) {
        return true;
    }

    if (isUnsetConfigValue(left) && isUnsetConfigValue(right)) {
        return true;
    }

    return JSON.stringify(left) === JSON.stringify(right);
}

function collectWorkflowConfigChanges(
    draft: WorkflowConfigMap,
    saved: WorkflowConfigMap,
): ClaimWorkflowConfigurationEditPayload[] {
    const changes: ClaimWorkflowConfigurationEditPayload[] = [];
    const statuses = new Set([...Object.keys(draft), ...Object.keys(saved)]);

    for (const workflowStatus of statuses) {
        const draftStatus = draft[workflowStatus] ?? {};
        const savedStatus = saved[workflowStatus] ?? {};
        const keys = new Set([...Object.keys(draftStatus), ...Object.keys(savedStatus)]);

        for (const key of keys) {
            const draftValue = draftStatus[key];
            const savedValue = savedStatus[key];

            if (!configValuesEqual(draftValue, savedValue)) {
                changes.push({
                    workflowStatus,
                    _configuration: key,
                    value: toPayloadValue(draftValue) as unknown as ClaimWorkflowConfigurationEditPayload['value'],
                });
            }
        }
    }

    return changes;
}

function hasPersistedConfiguration(
    configuration: WorkerWorkflowConfiguration | null,
    savedConfig: WorkflowConfigMap,
): boolean {
    return Boolean(configuration?.createDate) || Object.keys(savedConfig).length > 0;
}

export async function saveClaimWorkflowConfiguration(
    draftConfig: WorkflowConfigMap,
    savedConfig: WorkflowConfigMap,
    configuration: WorkerWorkflowConfiguration | null,
    apiMode: WorkerConfigurationApiMode = 'support',
): Promise<WorkerWorkflowConfiguration> {
    const api = CoreFetch<WorkerApi>(WorkerApi);

    if (!hasPersistedConfiguration(configuration, savedConfig)) {
        const requestBody: ClaimWorkflowConfigurationRequestBody = draftConfig;
        const created =
            apiMode === 'provider'
                ? await api.createClaimWorkflowConfigurationProvider({
                      requestBody: requestBody as CreateClaimWorkflowConfigurationRequest['requestBody'],
                  })
                : await api.createClaimWorkflowConfiguration({
                      requestBody: requestBody as CreateClaimWorkflowConfigurationRequest['requestBody'],
                  });
        return parseWorkerWorkflowConfiguration(created);
    }

    const changes = collectWorkflowConfigChanges(draftConfig, savedConfig);
    if (changes.length === 0) {
        const latest = await fetchClaimWorkflowConfiguration(apiMode);
        return parseWorkerWorkflowConfiguration(latest);
    }

    let latest: ClaimWorkflowConfiguration | undefined;
    for (const change of changes) {
        latest =
            apiMode === 'provider'
                ? await api.updateClaimWorkflowConfigurationProvider({
                      claimWorkflowConfigurationEditPayload: change,
                  })
                : await api.updateClaimWorkflowConfiguration({
                      claimWorkflowConfigurationEditPayload: change,
                  });
    }

    return parseWorkerWorkflowConfiguration(latest ?? (await fetchClaimWorkflowConfiguration(apiMode)));
}

export function createEmptyWorkflowConfigMap(): WorkflowConfigMap {
    return cloneWorkflowConfigMap();
}
